import { useState } from "react";
import { Link } from "react-router-dom";
import type { Product } from "@/lib/api";

type ProductColor = Product["colors"][number];
import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";

interface ProductCardProps {
  product: Product;
  showQuickAdd?: boolean;
}

export default function ProductCard({ product, showQuickAdd = true }: ProductCardProps) {
  const [hovered, setHovered] = useState(false);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState<ProductColor>(product.colors[0]);
  const [step, setStep] = useState<"idle" | "picking" | "added">("idle");
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();

  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const isSingleOption = product.sizes.length === 1 && product.colors.length === 1;

  const handleQuickAddClick = () => {
    if (isSingleOption) {
      doAddToCart(product.sizes[0], product.colors[0]);
    } else {
      setStep("picking");
    }
  };

  const doAddToCart = (size: string, color: ProductColor) => {
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      size,
      color: color.name,
      colorHex: color.hex,
      quantity: 1,
      image: product.images[0],
    });
    setStep("added");
    setTimeout(() => {
      setStep("idle");
      setSelectedSize("");
    }, 2000);
  };

  const handleConfirmAdd = () => {
    if (!selectedSize) return;
    doAddToCart(selectedSize, selectedColor);
  };

  const handleLeave = () => {
    setHovered(false);
    if (step !== "added") {
      setStep("idle");
      setSelectedSize("");
    }
  };

  return (
    <div
      className="group block"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleLeave}
    >
      {/* ── Image container: relative + overflow-hidden ──
          Quick Add panel lives inside here so it never
          bleeds outside the image bounds.               */}
      <div className="relative w-full overflow-hidden bg-pearl/10" style={{ aspectRatio: "3/4" }}>

        {/* Clickable image link fills the whole container */}
        <Link to={`/products/${product.slug}`} className="block w-full h-full cursor-pointer">
          {/* Main Image */}
          <img
            src={product.images[0]}
            alt={product.name}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 ${
              hovered && product.images[1] ? "opacity-0 scale-105" : "opacity-100 scale-100"
            }`}
          />
          {/* Hover Image */}
          {product.images[1] && (
            <img
              src={product.images[1]}
              alt={`${product.name} detail`}
              className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 ${
                hovered ? "opacity-100 scale-105" : "opacity-0 scale-100"
              }`}
            />
          )}
        </Link>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10 pointer-events-none">
          {product.isNew && (
            <span className="text-[10px] tracking-widest uppercase bg-midnight text-moonlight px-2 py-0.5 font-medium">
              New
            </span>
          )}
          {hasDiscount && (
            <span className="text-[10px] tracking-widest uppercase bg-moon-gold-dark text-midnight px-2 py-0.5 font-medium">
              Sale
            </span>
          )}
        </div>

        {/* Fabric Label */}
        <div className="absolute top-3 right-3 z-10 pointer-events-none">
          <span className="text-[10px] tracking-wide bg-moonlight/80 text-midnight/70 px-2 py-0.5 backdrop-blur-sm">
            {product.fabricLabel}
          </span>
        </div>

        {/* Wishlist Heart Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleWishlist(product.id);
          }}
          className={`absolute bottom-3 right-3 z-40 w-8 h-8 flex items-center justify-center bg-moonlight/85 backdrop-blur-sm hover:bg-moonlight transition-all duration-200 cursor-pointer ${
            hovered || isWishlisted(product.id) ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          title={isWishlisted(product.id) ? "Remove from wishlist" : "Save to wishlist"}
        >
          <i
            className={`text-sm transition-colors duration-200 ${
              isWishlisted(product.id)
                ? "ri-heart-fill text-rose-400"
                : "ri-heart-line text-midnight/60 hover:text-rose-400"
            }`}
          />
        </button>

        {/* Added Success Overlay */}
        <div
          className={`absolute inset-0 z-20 flex flex-col items-center justify-center transition-opacity duration-300 pointer-events-none ${
            step === "added" ? "opacity-100" : "opacity-0"
          }`}
          style={{ background: "rgba(26,42,50,0.55)", backdropFilter: "blur(2px)" }}
        >
          <div className="w-12 h-12 rounded-full bg-moonlight flex items-center justify-center mb-2">
            <i className="ri-check-line text-xl text-midnight" />
          </div>
          <p className="text-[11px] tracking-widest uppercase text-moonlight font-medium">Added to Cart</p>
        </div>

        {/* Quick Add Panel — inside image container, clipped by overflow-hidden */}
        {showQuickAdd && step !== "added" && (
          <div
            className={`absolute bottom-0 left-0 right-0 z-30 transition-transform duration-300 ease-out ${
              hovered ? "translate-y-0" : "translate-y-full"
            }`}
            style={{ background: "rgba(249,246,240,0.97)", backdropFilter: "blur(8px)" }}
          >
            {step === "picking" ? (
              <div className="px-3 pt-3 pb-3">
                {/* Color Swatches */}
                {product.colors.length > 1 && (
                  <div className="mb-2.5">
                    <p className="text-[9px] tracking-widest uppercase text-midnight/40 mb-1.5">Color</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {product.colors.map((c) => (
                        <button
                          key={c.name}
                          onClick={() => setSelectedColor(c)}
                          title={c.name}
                          className={`w-5 h-5 rounded-full border-2 transition-all duration-150 cursor-pointer ${
                            selectedColor.name === c.name
                              ? "border-midnight scale-110"
                              : "border-transparent hover:border-midnight/40"
                          }`}
                          style={{ backgroundColor: c.hex, outline: "1px solid rgba(0,0,0,0.1)" }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Size Buttons */}
                <div className="mb-2.5">
                  <p className="text-[9px] tracking-widest uppercase text-midnight/40 mb-1.5">Size</p>
                  <div className="flex gap-1 flex-wrap">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`text-[10px] min-w-[28px] h-7 px-1.5 flex items-center justify-center border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                          selectedSize === size
                            ? "border-midnight bg-midnight text-moonlight"
                            : "border-pearl/70 text-midnight/60 hover:border-midnight hover:text-midnight"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Confirm Add */}
                <button
                  onClick={handleConfirmAdd}
                  disabled={!selectedSize}
                  className={`w-full text-[10px] tracking-widest uppercase py-2.5 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    selectedSize
                      ? "bg-midnight text-moonlight hover:bg-midnight/90"
                      : "bg-pearl/40 text-midnight/30 cursor-not-allowed"
                  }`}
                >
                  {selectedSize ? "Add to Cart" : "Select a Size"}
                </button>
              </div>
            ) : (
              <button
                onClick={handleQuickAddClick}
                className="w-full text-[10px] tracking-widest uppercase py-3 text-midnight/70 hover:text-midnight hover:bg-pearl/40 transition-colors duration-150 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
              >
                <i className="ri-shopping-bag-line text-xs" />
                Quick Add
              </button>
            )}
          </div>
        )}
      </div>

      {/* Clickable Product Info */}
      <Link to={`/products/${product.slug}`} className="block cursor-pointer">
        <div className="mt-3 px-0.5" style={{ fontFamily: "'Jost', sans-serif" }}>
          <p className="text-xs tracking-wide text-midnight/80 group-hover:text-midnight transition-colors line-clamp-1">
            {product.name}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm font-medium text-midnight">${product.price}</span>
            {hasDiscount && (
              <span className="text-xs text-midnight/40 line-through">${product.originalPrice}</span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-1">
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <i
                  key={i}
                  className={`text-[9px] ${
                    i < Math.floor(product.rating)
                      ? "ri-star-fill text-moon-gold-dark"
                      : "ri-star-line text-pearl"
                  }`}
                />
              ))}
            </div>
            <span className="text-[10px] text-midnight/40">({product.reviewCount})</span>
          </div>
          {product.colors.length > 1 && (
            <div className="flex items-center gap-1 mt-1.5">
              {product.colors.slice(0, 4).map((c) => (
                <span
                  key={c.name}
                  title={c.name}
                  className="w-2.5 h-2.5 rounded-full border border-pearl/40"
                  style={{ backgroundColor: c.hex }}
                />
              ))}
              {product.colors.length > 4 && (
                <span className="text-[9px] text-midnight/30">+{product.colors.length - 4}</span>
              )}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}
